import { useMemo } from 'react';
import { useMetadataStore } from '@/entities/metadata/model/metadataStore';
import type { UserScreenNode } from '@/shared/types/system';

/** Menü ağacı + favoriler + düz liste (spotlight/arama için). */
export function useMenu() {
  const tree = useMetadataStore((s) => s.menuTree);
  const loaded = useMetadataStore((s) => s.isMenuLoaded);
  const flat = useMemo(() => {
    const out: (UserScreenNode & { path: string[] })[] = [];
    const walk = (nodes: UserScreenNode[], path: string[]) => {
      for (const n of nodes) {
        out.push({ ...n, path });
        if (n.children.length) walk(n.children, [...path, n.caption]);
      }
    };
    walk(tree, []);
    return out;
  }, [tree]);
  const favorites = useMemo(
    () => flat.filter((n) => n.favorite && n.objectId && n.accessible !== false),
    [flat],
  );
  return { tree, flat, favorites, loaded };
}

/**
 * Tıklanınca ekran açan düğüm mü? Yetkisi olmayan üst modüller (accessible=false) asla
 * yaprak sayılmaz: yalnızca gezinmek için duruyorlar, tıklanınca açılacak bir ekran yok.
 */
export function isLeaf(node: UserScreenNode) {
  if (node.accessible === false) return false;
  return (
    node.children.length === 0 &&
    (node.objectId != null || (node.screenTypeName ?? '').toLowerCase() !== 'mainmenu')
  );
}

export function normalize(s: string) {
  return s.toLocaleLowerCase('tr').replace(/İ/g, 'i').replace(/I/g, 'ı');
}

/** Arama: eşleşen düğümler + ataları korunur. */
export function filterTree(nodes: UserScreenNode[], q: string): UserScreenNode[] {
  if (!q) return nodes;
  const nq = normalize(q);
  const walk = (list: UserScreenNode[]): UserScreenNode[] =>
    list
      .map((n) => {
        const children = walk(n.children);
        const hit = normalize(n.caption).includes(nq);
        if (hit || children.length) return { ...n, children: hit ? n.children : children };
        return null;
      })
      .filter((n): n is UserScreenNode => n !== null);
  return walk(nodes);
}
